import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";
import { HelpTooltip } from "../cms/HelpTooltip";
import type { ProductMatch } from "./TakeoffOrderDialog";
import { normalizeUnit, unitLabel } from "./takeoffQuantity";

type SearchPage = {
  results: ProductMatch[];
  page: number;
  has_more: boolean;
  total: number | null;
};
export function TakeoffProductPicker({
  reviewId,
  material,
  takeoffUnit,
  initialSearch,
  onSelect,
  onClose,
}: {
  reviewId: number;
  material: string;
  takeoffUnit: string;
  initialSearch: string;
  onSelect: (product: ProductMatch) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState(initialSearch);
  const [request, setRequest] = useState({
    query: initialSearch,
    page: 1,
    attempt: 0,
  });
  const [data, setData] = useState<SearchPage | null>(null);
  const [busy, setBusy] = useState(initialSearch.trim().length >= 2);
  const [error, setError] = useState("");
  const beginSearch = (next: typeof request) => {
    setData(null);
    setError("");
    setBusy(next.query.trim().length >= 2);
    setRequest(next);
  };
  useEffect(() => {
    let current = true;
    if (request.query.trim().length < 2) return;
    apiClient
      .get<SearchPage>(
        `/api/distributors/takeoff-orders/${reviewId}/products/?q=${encodeURIComponent(request.query.trim())}&page=${request.page}`,
      )
      .then((response) => {
        if (current) setData(response.data);
      })
      .catch(() => {
        if (current)
          setError(
            "The catalog could not be loaded. Your selected product is unchanged. Please try again.",
          );
      })
      .finally(() => {
        if (current) setBusy(false);
      });
    return () => {
      current = false;
    };
  }, [reviewId, request]);
  const grouped = new Map<string, ProductMatch[]>();
  data?.results.forEach((product) => {
    const options = grouped.get(product.key) || [];
    options.push(product);
    grouped.set(product.key, options);
  });
  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        aria-labelledby="takeoff-product-picker-title"
        sx={{
          width: "min(920px, 96vw)",
          maxHeight: "90dvh",
          p: 0,
          gap: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            pr: 6,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <ModalClose aria-label="Close product search" />
          <DialogTitle id="takeoff-product-picker-title">
            Find a product
          </DialogTitle>
          <Typography level="body-sm">
            Choose a product for {material}. Check the brand, color and ordering
            unit.
          </Typography>
        </Box>
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            beginSearch({
              query: search,
              page: 1,
              attempt: request.attempt + 1,
            });
          }}
          sx={{ p: 2, bgcolor: "background.level1", flexShrink: 0 }}
        >
          <FormControl>
            <FormLabel>
              Search your distributor’s catalog{" "}
              <HelpTooltip
                title="Search the full catalog"
                content="Search by product name, brand, color or SKU. Try fewer words for more results. Use the page controls to browse every match."
              />
            </FormLabel>
            <Stack direction="row" gap={1}>
              <Input
                autoFocus
                aria-label="Search catalog"
                placeholder="Brand, product, color or SKU…"
                startDecorator={<MagnifyingGlass />}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Button type="submit" disabled={search.trim().length < 2}>
                Search
              </Button>
            </Stack>
          </FormControl>
        </Box>
        <Stack
          spacing={1.5}
          sx={{ overflowY: "auto", minHeight: 0, flex: 1, p: 2 }}
          aria-busy={busy}
        >
          {busy && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress aria-label="Searching catalog" />
            </Box>
          )}
          {error && (
            <Alert color="danger">
              <Stack spacing={1}>
                {error}
                <Button
                  variant="outlined"
                  color="danger"
                  onClick={() =>
                    beginSearch({ ...request, attempt: request.attempt + 1 })
                  }
                >
                  Retry search
                </Button>
              </Stack>
            </Alert>
          )}
          {!busy && data && (
            <Typography role="status" level="body-sm">
              {data.total == null
                ? `${grouped.size} products on this page`
                : `${data.total} matching products`}{" "}
              · Results for “{request.query}”
            </Typography>
          )}
          {!busy && data && !grouped.size && (
            <Alert color="neutral">
              No products found. Try a brand, a shorter product name, or an
              exact SKU.
            </Alert>
          )}
          {!busy && !data && !error && (
            <Typography>Enter at least 2 characters to search.</Typography>
          )}
          {[...grouped].map(([key, options]) => (
            <Card key={key} variant="outlined" sx={{ flexShrink: 0 }}>
              <Typography level="title-md">{options[0].name}</Typography>
              <Typography level="body-xs">
                SKU {options[0].sku || "Not supplied"}
              </Typography>
              <Stack
                direction="row"
                gap={1}
                flexWrap="wrap"
                alignItems="center"
              >
                <Typography level="body-sm">Order by:</Typography>
                {[...options]
                  .sort(
                    (a, b) =>
                      Number(
                        normalizeUnit(b.unit) === normalizeUnit(takeoffUnit),
                      ) -
                      Number(
                        normalizeUnit(a.unit) === normalizeUnit(takeoffUnit),
                      ),
                  )
                  .map((product) => (
                    <Button
                      key={product.unit}
                      variant="outlined"
                      onClick={() => onSelect(product)}
                    >
                      Select {unitLabel(product.unit)} ({product.unit})
                      {normalizeUnit(product.unit) ===
                        normalizeUnit(takeoffUnit) && (
                        <Chip size="sm" color="success">
                          Matches takeoff
                        </Chip>
                      )}
                    </Button>
                  ))}
              </Stack>
            </Card>
          ))}
        </Stack>
        <Stack
          direction="row"
          gap={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            disabled={busy || request.page <= 1}
            onClick={() => beginSearch({ ...request, page: request.page - 1 })}
          >
            Previous
          </Button>
          <Typography level="body-sm">Page {request.page}</Typography>
          <Button
            variant="outlined"
            disabled={busy || !data?.has_more}
            onClick={() => beginSearch({ ...request, page: request.page + 1 })}
          >
            Next
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
